// Trigger action picker — replaces the flat 8-button chip row in the
// New-Trigger / Edit-Trigger dialog.
//
// Groups the eight action types into five families with one-line blurbs,
// so an operator picking 'derive' vs 'reingest' vs 'enrich_fetch' for the
// first time sees what each does without reading docs. The picked
// action's blurb stays visible below as an amber info strip — same
// content the body of the modal previously hid until the operator
// started filling fields.
//
// `flatten` was added in Apr 2026 (architecture correction): events
// always enter with their original shape; flatten emits a derivative
// event with leaf paths as top-level keys for downstream consumption.
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import React from 'react';

export type TriggerActionType =
  | 'forward_webhook'
  | 'forward_webhook_batched'
  | 'promote_to_table'
  | 'validate_required'
  | 'validate_dedupe'
  | 'enrich_fetch';

export const ACTION_META: Record<TriggerActionType, { group: 'Validate' | 'Materialize' | 'Forward' | 'Enrich'; label: string; blurb: string; icon: string }> = {
  validate_required:  { group: 'Validate',    label: 'validate_required',  icon: '✓',   blurb: 'Fail the run if any listed field is missing or empty. Bad event quarantines: downstream triggers skip it.' },
  validate_dedupe:    { group: 'Validate',    label: 'validate_dedupe',    icon: '⌱',   blurb: 'Sliding-window dedupe on a payload key. Second event with the same key inside the window quarantines.' },
  promote_to_table:   { group: 'Materialize', label: 'promote_to_table',   icon: '⤓',   blurb: 'Create a BigQuery view over the source\'s GCS objects. Zero fixed storage cost — pay only on query scan. New events arriving in GCS are auto-included on the next query. Filter at query time via WHERE.' },
  forward_webhook:    { group: 'Forward',     label: 'forward_webhook',    icon: '↗',   blurb: 'POST the payload to an external URL with HMAC-signed headers. Retries 3 times. SSRF-blocked + self-loop guard.' },
  forward_webhook_batched: { group: 'Forward', label: 'forward_webhook_batched', icon: '⇒', blurb: 'POST N events per outbound HTTP request (default 100). Designed for ?bulk=true uploads where per-event forward_webhook would burn rate budgets. Body shape {events:[...], batch_index, batch_total} with HMAC-signed envelope.' },
  enrich_fetch:       { group: 'Enrich',      label: 'enrich_fetch',       icon: '✦',   blurb: 'GET an external URL using payload[id_path] for {id} substitution; merge JSON response back into payload at merge_path. Emits a derivative event.' },
};

export function ActionPicker({ type, onChange }: { type: TriggerActionType; onChange: (t: TriggerActionType) => void }): React.ReactElement {
  // Order intentional: most-used first within each group.
  const ORDER: TriggerActionType[] = ['validate_required', 'validate_dedupe', 'promote_to_table', 'forward_webhook', 'forward_webhook_batched', 'enrich_fetch'];
  const groups = new Map<string, TriggerActionType[]>();
  for (const t of ORDER) {
    const g = ACTION_META[t].group;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(t);
  }
  const meta = ACTION_META[type];
  return (
    <div style={{ marginBottom: 12 }}>
      {[...groups.entries()].map(([group, types]) => (
        <div key={group} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: 86 }}>
            {group}
          </span>
          {types.map(t => {
            const m = ACTION_META[t];
            const active = type === t;
            return (
              <button
                key={t}
                onClick={() => onChange(t)}
                title={m.blurb}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', fontSize: 12, fontFamily: 'var(--mono)',
                  background: active ? 'var(--ink)' : 'transparent',
                  color: active ? 'var(--bg)' : 'var(--ink-2)',
                  border: `1px solid ${active ? 'var(--ink)' : 'var(--line)'}`,
                  borderRadius: 4, cursor: 'pointer',
                }}
              >
                <span aria-hidden="true">{m.icon}</span>
                {m.label}
              </button>
            );
          })}
        </div>
      ))}
      <div style={{
        marginTop: 4, padding: '8px 12px',
        background: 'var(--accent-soft)', border: '1px solid var(--accent-line)',
        borderRadius: 6, fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5,
      }}>
        <span style={{ marginRight: 6 }}>{meta.icon}</span>
        <strong style={{ color: 'var(--ink)' }}>{meta.label}</strong> — {meta.blurb}
      </div>
    </div>
  );
}
